import {useNavigate, useParams} from "react-router-dom";
import {offerApi} from "../services/offerService";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {offerStatus, userRoleList} from "../utils/constants";
import {showToastModal} from "../jsx/components/shared/ToastModalSwal";

export const useFetchOffers = (
  {size, filter, order, search},
  role,
  {enabled = true} = {},
) => {
  //
  //
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isSuccess,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "offers",
      size,
      JSON.stringify(filter),
      JSON.stringify(order),
      search,
      role,
    ],
    queryFn: ({pageParam, signal}) =>
      role === userRoleList.CLIENT
        ? offerApi.getMe({size, filter, order, page: pageParam, search, signal})
        : offerApi.getAll({
            size,
            filter,
            order,
            page: pageParam,
            search,
            signal,
          }),
    initialPageParam: 0,
    enabled: enabled && !!role,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    getNextPageParam: (lastPage, allPages) => {
      const morePagesExist =
        lastPage?.currentPage < lastPage?.totalPages - 1;
      if (!morePagesExist) return undefined;
      return allPages.length; // The next page index
    },
    select: (data) => {
      // Keep original pages structure for pagination, add flatPages for rendering
      return {
        ...data,
        flatPages: data?.pages
          ? data.pages.flatMap((page) => page?.rows || [])
          : [],
      };
    },
  });

  return {
    offers: data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isSuccess,
    refetch,
  };
};

export const useFetchMeOffers = ({size, filter, order, search}) => {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "meOffers",
      size,
      JSON.stringify(filter),
      JSON.stringify(order),
      search,
    ],
    queryFn: ({pageParam, signal}) =>
      offerApi.getMe({
        size,
        filter,
        order,
        page: pageParam,
        search,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const morePagesExist =
        lastPage?.currentPage < lastPage?.totalPages - 1;
      if (!morePagesExist) return undefined;
      return allPages.length; // The next page index
    },
    select: (data) => {
      // Keep original pages structure for pagination, add flatPages for rendering
      return {
        ...data,
        flatPages: data?.pages
          ? data.pages.flatMap((page) => page?.rows || [])
          : [],
      };
    },
  });

  return {
    offers: data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

export const useFetchfavOffers = ({size}) => {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["favOffers", size],
    queryFn: ({pageParam, signal}) =>
      offerApi.getFavorites({
        size,

        page: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const morePagesExist = lastPage.currentPage < lastPage.totalPages - 1;
      if (!morePagesExist) return undefined;
      return allPages.length; // The next page index
    },
    select: (data) => {
      // Keep original pages structure for pagination, add flatPages for rendering
      return {
        ...data,
        flatPages: data?.pages
          ? data.pages.flatMap((page) => page?.rows || [])
          : [],
      };
    },
  });

  return {
    offers: data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

export const useOffer = () => {
  const {id: idUrl} = useParams();
  const id = idUrl.split("-")[0];

  const {data, isLoading, isError, error, refetch} = useQuery({
    queryKey: ["offer", id],
    queryFn: () => offerApi.getOne({id}),
    enabled: !!id,
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useAddOffer = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    mutate: addOffer,
    mutateAsync: addOfferAsync,
    isPending: isAdding,
  } = useMutation({
    mutationFn: offerApi.add,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["offer"]});
      return showToastModal({
        success: true,
        title: "Annonce ajoutée",
        message:
          "Votre annonce a été ajoutée avec succès, elle sera validée par l’équipe Helpclic dans les plus brefs délais.",
        buttonTitle: "Fermer",
        thenCallback: () => navigate("/"),
      });
    },
  });

  const {mutate: deleteOffer} = useMutation({
    mutationFn: offerApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["offer"]});
      showToastModal({
        success: true,
        title: "Annonce supprimée",
        message: "Votre annonce a été supprimée avec succès",
        buttonTitle: "Fermer",
      });
      // showToastModal({
      //   success: true,
      //   title: "Annonce ajoutée",
      //   message:
      //     "Votre annonce a été ajoutée avec succès, elle sera valider par l’équipe Helpclic dans les plus brefs délais.",
      //   buttonTitle: "Fermer",
      // });
    },
  });

  const {mutate: updateOffer, mutateAsync: updateOfferAsync} = useMutation({
    mutationFn: offerApi.update,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["offer"]});
      return showToastModal({
        success: true,
        title: "Annonce modifiée",
        message:
          "Votre annonce a été modifiée avec succès, elle sera valider par l’équipe Helpclic dans les plus brefs délais.",
        buttonTitle: "Fermer",
        thenCallback: variables?.afterModalClose,
      });
    },
  });

  const {mutate: addFavorite} = useMutation({
    mutationFn: offerApi.addFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["favOffers"]});
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["offer"]});
      showToastModal({
        success: true,
        title: "Succès",
        message: "L'annonce a été ajoutée à la liste des favoris avec succès.",
        buttonTitle: "Fermer",
      });
    },
  });
  const {mutate: removeFavorite} = useMutation({
    mutationFn: offerApi.removeFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["favOffers"]});
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["offer"]});
      showToastModal({
        success: true,
        title: "Succès",
        message: "L'annonce a été retirée de la liste des favoris avec succès.",
        buttonTitle: "Fermer",
      });
    },
  });

  const onAddFavorite = ({id}) => {
    addFavorite({id});
  };
  const onRemoveFavorite = ({id}) => {
    removeFavorite({id});
  };

  const onDeleteOffer = ({id}) => {
    deleteOffer({id});
  };
  const getUpdateOfferVariables = ({
    id,
    body,
    afterModalClose,
    onUploadProgress,
  }) => {
    const formdata = new FormData();
    const {files, subCategory, ...values} = body;

    // Append all values to formdata
    Object.entries(values).forEach(([key, value]) => {
      formdata.append(key, value);
    });
    if (!!files) {
      files.forEach((el) => {
        formdata.append("offerImg", el.file);
      });
    }
    if (!!subCategory) {
      subCategory.forEach((el) => {
        formdata.append("subCategoryId", el);
      });
    }
    // formdata.append("subCategoryId", JSON.stringify(subCategory));
    return {id, body: formdata, afterModalClose, onUploadProgress};
  };
  const onUpdateOffer = (values) => {
    updateOffer(getUpdateOfferVariables(values));
  };
  const onUpdateOfferAsync = (values) => {
    return updateOfferAsync(getUpdateOfferVariables(values));
  };

  const onActivateOffer = ({id}) => {
    updateOffer(
      {
        id,
        body: {
          status: offerStatus.PUBLIC,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showToastModal({
            success: true,
            title: "Annonce activée",
            message: "Votre annonce a été activée avec succès",
            buttonTitle: "Fermer",
          });
        },
      },
    );
  };

  const onHideOffer = ({id}) => {
    updateOffer(
      {
        id,
        body: {
          status: offerStatus.DISABLED,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ["offers"]});
          showToastModal({
            success: true,
            title: "Annonce désactivée",
            message: "Votre annonce a été désactivée avec succès.",
            buttonTitle: "Fermer",
          });
        },
      },
    );
  };

  const getAddOfferVariables = ({body, onUploadProgress}) => {
    const formdata = new FormData();
    const {files, subCategory, ...values} = body;
    // Append all values to formdata
    Object.entries(values).forEach(([key, value]) => {
      formdata.append(key, value);
    });
    files.forEach((el) => {
      formdata.append("offerImg", el.file);
    });
    formdata.append("subCategoryId", JSON.stringify(subCategory));
    return {body: formdata, onUploadProgress};
  };
  const onAddOffer = (values) => {
    addOffer(getAddOfferVariables(values));
  };
  const onAddOfferAsync = (values) => {
    return addOfferAsync(getAddOfferVariables(values));
  };

  return {
    onAddOffer,
    onAddOfferAsync,
    onAddFavorite,
    onDeleteOffer,
    onUpdateOffer,
    onUpdateOfferAsync,
    onRemoveFavorite,
    onActivateOffer,
    onHideOffer,
    isAdding,
  };
};
